/**
 * Tags hooks using React Relay
 *
 * These hooks provide access to support ticket tag data through Relay's
 * normalized cache and automatic data management.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useLazyLoadQuery,
  usePaginationFragment,
  useQueryLoader,
  usePreloadedQuery,
  PreloadedQuery,
  fetchQuery,
} from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useTagsRelayQuery as UseTagsRelayQuery,
  useTagsRelayQuery$variables,
  SupportTicketTagFilterInput,
  SupportTicketTagSortInput,
} from './__generated__/useTagsRelayQuery.graphql';
import type { useTagsRelayDirectQuery as UseTagsRelayDirectQuery } from './__generated__/useTagsRelayDirectQuery.graphql';
import type { useTagsRelayPaginationFragment$key } from './__generated__/useTagsRelayPaginationFragment.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Pagination fragment for tags list
 */
const TagsListPaginationFragment = graphql`
  fragment useTagsRelayPaginationFragment on RootQueryType
  @refetchable(queryName: "useTagsRelayPaginationQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 100 }
    after: { type: "String" }
    filter: { type: "SupportTicketTagFilterInput" }
    sort: { type: "[SupportTicketTagSortInput!]" }
  ) {
    listSupportTicketTags(first: $first, after: $after, filter: $filter, sort: $sort)
      @connection(
        key: "useTagsRelayPaginationFragment_listSupportTicketTags"
        filters: ["filter", "sort"]
      ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          name
          createdOn
          archivedOn
          system {
            id
            oldId
            name
          }
          systemId
          organizationId
        }
      }
    }
  }
`;

/**
 * Query for paginated tags list (uses fragment for Suspense hooks)
 */
const TagsListQuery = graphql`
  query useTagsRelayQuery(
    $first: Int
    $after: String
    $filter: SupportTicketTagFilterInput
    $sort: [SupportTicketTagSortInput!]
  ) {
    ...useTagsRelayPaginationFragment
      @arguments(first: $first, after: $after, filter: $filter, sort: $sort)
  }
`;

/**
 * Direct query for tags list (for non-Suspense fetchQuery usage)
 * This query doesn't use fragments so fetchQuery returns the actual data
 */
const TagsListDirectQuery = graphql`
  query useTagsRelayDirectQuery(
    $first: Int
    $after: String
    $filter: SupportTicketTagFilterInput
    $sort: [SupportTicketTagSortInput!]
  ) {
    listSupportTicketTags(
      first: $first
      after: $after
      filter: $filter
      sort: $sort
    ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          name
          createdOn
          archivedOn
          system {
            id
            oldId
            name
          }
          systemId
          organizationId
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { SupportTicketTagFilterInput, SupportTicketTagSortInput };

/**
 * System type from GraphQL tag
 */
export interface TagSystemGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
}

/**
 * Tag type from GraphQL - matches the fragment fields
 */
export interface TagGraphQL {
  id: string;
  name: string;
  createdOn: string;
  archivedOn?: string | null;
  system?: TagSystemGraphQL | null;
  systemId?: string | null;
  organizationId?: string | null;
}

/**
 * Simplified filter options for common use cases
 */
export interface TagQueryFilter {
  systemId?: string;
  organizationId?: string;
  archived?: boolean; // Filter by archived status
  name?: string; // Filter by name
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching tags list using Relay
 *
 * This hook uses Relay's useLazyLoadQuery which suspends while loading.
 * Wrap with Suspense boundary.
 *
 * @example
 * function TagsList() {
 *   const { data, loadNext, hasNext, isLoadingNext } = useTagsRelay({
 *     systemId: 'uuid',
 *   });
 *
 *   return (
 *     <>
 *       {data.map(tag => <div key={tag.id}>{tag.name}</div>)}
 *       {hasNext && <button onClick={() => loadNext(100)}>Load More</button>}
 *     </>
 *   );
 * }
 *
 * // With Suspense
 * <Suspense fallback={<Loading />}>
 *   <TagsList />
 * </Suspense>
 */
export function useTagsRelay(
  filter?: TagQueryFilter,
  options?: {
    limit?: number;
    sort?: SupportTicketTagSortInput[];
  }
) {
  const limit = options?.limit ?? 100;
  const sort = options?.sort ?? [{ field: 'CREATED_ON', order: 'DESC' }];

  const builtFilter = buildRelayFilter(filter);
  const variables: useTagsRelayQuery$variables = {
    first: limit,
    sort,
    // Only include filter if it's defined (not null/undefined)
    ...(builtFilter ? { filter: builtFilter } : {}),
  };

  // Initial query - suspends while loading
  const queryData = useLazyLoadQuery<UseTagsRelayQuery>(TagsListQuery, variables);

  // Pagination fragment for load more
  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseTagsRelayQuery,
    useTagsRelayPaginationFragment$key
  >(TagsListPaginationFragment, queryData);

  // Extract nodes from edges
  const nodes =
    data.listSupportTicketTags?.edges?.map((edge) => edge?.node).filter(Boolean) ??
    [];

  return {
    data: nodes as TagGraphQL[],
    count: data.listSupportTicketTags?.count,
    pageInfo: data.listSupportTicketTags?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

// ============================================================================
// Non-Suspense Hooks (with loading state)
// ============================================================================

/**
 * Hook that returns a query loader for tags list
 *
 * This provides explicit loading state without requiring Suspense.
 * Call `loadQuery` to start fetching, then pass `queryRef` to a child component.
 */
export function useTagsRelayLoader(initialFilter?: TagQueryFilter) {
  const initialBuiltFilter = buildRelayFilter(initialFilter);
  const [queryRef, loadQuery, disposeQuery] = useQueryLoader<UseTagsRelayQuery>(
    TagsListQuery,
    initialFilter
      ? {
          first: 100,
          sort: [{ field: 'CREATED_ON', order: 'DESC' }],
          ...(initialBuiltFilter ? { filter: initialBuiltFilter } : {}),
        }
      : undefined
  );

  const load = (
    filter?: TagQueryFilter,
    options?: { limit?: number; sort?: SupportTicketTagSortInput[] }
  ) => {
    const limit = options?.limit ?? 100;
    const sort = options?.sort ?? [{ field: 'CREATED_ON', order: 'DESC' }];
    const builtFilter = buildRelayFilter(filter);

    loadQuery({
      first: limit,
      sort,
      ...(builtFilter ? { filter: builtFilter } : {}),
    });
  };

  return {
    queryRef,
    loadQuery: load,
    disposeQuery,
    isLoading: queryRef === null,
  };
}

/**
 * Hook to read data from a preloaded tags query
 *
 * Use with useTagsRelayLoader. Still suspends, but the data
 * is already loading so suspension is minimal.
 */
export function useTagsRelayPreloaded(queryRef: PreloadedQuery<UseTagsRelayQuery>) {
  const queryData = usePreloadedQuery<UseTagsRelayQuery>(TagsListQuery, queryRef);

  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseTagsRelayQuery,
    useTagsRelayPaginationFragment$key
  >(TagsListPaginationFragment, queryData);

  const nodes =
    data.listSupportTicketTags?.edges?.map((edge) => edge?.node).filter(Boolean) ??
    [];

  return {
    data: nodes as TagGraphQL[],
    count: data.listSupportTicketTags?.count,
    pageInfo: data.listSupportTicketTags?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

// ============================================================================
// React Query-like Interface (No Suspense)
// ============================================================================

/**
 * Hook that provides React Query-like interface for tags data
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 * similar to React Query's useQuery.
 *
 * @example
 * const { data, isLoading, refetch, isFetched, count, hasMore } = useTagsRelayQuery({
 *   systemId: 'uuid',
 * });
 *
 * if (isLoading) return <Loading />;
 * return <div>{data?.map(t => t.name)}</div>;
 */
export function useTagsRelayQuery(
  filter?: TagQueryFilter,
  options?: {
    limit?: number;
    sort?: SupportTicketTagSortInput[];
    enabled?: boolean;
    after?: string | null;
  }
) {
  const [data, setData] = useState<TagGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [endCursor, setEndCursor] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const limit = options?.limit ?? 100;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'CREATED_ON' as const, order: 'DESC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;
  // Convert null to undefined to avoid sending null to GraphQL
  const after = options?.after || undefined;

  // Serialize filter to stable string for dependency comparison
  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const builtFilter = buildRelayFilter(filter);
        // Build variables object, explicitly excluding null/undefined values
        // to prevent GraphQL from receiving null parameters
        const variables: Record<string, unknown> = {
          first: limit,
          sort,
        };
        if (after) {
          variables.after = after;
        }
        if (builtFilter) {
          variables.filter = builtFilter;
        }

        const result = await fetchQuery<UseTagsRelayDirectQuery>(
          relayEnvironment,
          TagsListDirectQuery,
          variables as useTagsRelayQuery$variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listSupportTicketTags?.edges
              ?.map((edge) => edge?.node)
              .filter(Boolean) ?? [];
          setData(nodes as TagGraphQL[]);
          setCount(result.listSupportTicketTags?.count ?? undefined);
          setHasMore(result.listSupportTicketTags?.pageInfo?.hasNextPage ?? false);
          setEndCursor(
            result.listSupportTicketTags?.pageInfo?.endCursor ?? undefined
          );
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch tags'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, sort, after, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    hasMore,
    endCursor,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Builds a Relay/GraphQL SupportTicketTagFilterInput from the simplified filter interface
 */
function buildRelayFilter(
  filter?: TagQueryFilter
): SupportTicketTagFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: SupportTicketTagFilterInput = {};

  if (filter.systemId) {
    relayFilter.systemId = { eq: filter.systemId };
  }
  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }
  // Filter by archived status
  if (filter.archived !== undefined) {
    if (filter.archived) {
      // Show only archived tags
      relayFilter.archivedOn = { isNil: false };
    } else {
      // Show only non-archived tags (default behavior)
      relayFilter.archivedOn = { isNil: true };
    }
  }
  // Filter by name
  if (filter.name) {
    relayFilter.name = { ilike: `%${filter.name}%` };
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}

export { buildRelayFilter };
